import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

// Manager view of what the winder counted on a given day: full spools credited
// automatically, incomplete spools (waste) with estimated mass broken down by line /
// product / operator / shift, and cycles that could not be classified.

interface CycleRow {
  id: number;
  line_name: string | null;
  started_at: string;
  ended_at: string;
  length_m: number;
  nominal_length_m: number | null;
  classification: 'full' | 'over' | 'partial' | 'unknown';
  counted: boolean;
  operator_confirmed_partial: boolean | null;
  estimated_weight_kg: number | null;
  shift: string | null;
  user_name: string | null;
  item: { material: string | null; kolor: string | null; indeks_kat: string | null; srednica: string | null; waga: string | null } | null;
}

interface Summary { cycles: number; counted: number; partial_pending: number; waste_count: number; waste_kg: number; unknown: number }
interface WasteGroup { key: string | number; label: string; count: number; kg: number }
interface LineOption { id: number; name: string }

const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
const fmt = (v: number | null, d = 0) => v === null ? '—' : v.toLocaleString('pl-PL', { minimumFractionDigits: d, maximumFractionDigits: d });

const WinderCycles: React.FC = () => {
  const { t } = useLanguage();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [lineId, setLineId] = useState('');
  const [lines, setLines] = useState<LineOption[]>([]);
  const [rows, setRows] = useState<CycleRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [wasteBy, setWasteBy] = useState<Record<'line' | 'product' | 'operator' | 'shift', WasteGroup[]> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/production-lines').then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data.lines ?? res.data.data ?? []);
      setLines(list.map((l: any) => ({ id: l.id, name: l.external_code || l.name })));
    }).catch(() => {});
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/winder-cycles', { params: { date, line_id: lineId || undefined } });
      setRows(res.data.cycles);
      setSummary(res.data.summary);
      setWasteBy(res.data.waste_by);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [date, lineId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const classBadge = (r: CycleRow) => {
    if (r.classification === 'full' || r.classification === 'over') return <span style={{ ...badge, background: '#d1fae5', color: '#065f46' }}>✔ +1 {t('winder.full')}</span>;
    if (r.classification === 'partial') {
      if (r.operator_confirmed_partial === true) return <span style={{ ...badge, background: '#fef3c7', color: '#92400e' }}>🧵 {t('winder.waste')}</span>;
      if (r.operator_confirmed_partial === false) return <span style={{ ...badge, background: '#e5e7eb', color: '#475569' }}>{t('winder.notASpool')}</span>;
      return <span style={{ ...badge, background: '#fff3cd', color: '#92400e' }}>⏳ {t('winder.awaitingOperator')}</span>;
    }
    return <span style={{ ...badge, background: '#fee2e2', color: '#991b1b' }}>? {t('winder.unknown')}</span>;
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>🎞️ {t('winder.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('winder.subtitle')}</p>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px' }}>
        <label style={lbl}>{t('winder.date')}<input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} /></label>
        <label style={lbl}>{t('winder.line')}
          <select value={lineId} onChange={e => setLineId(e.target.value)} style={inp}>
            <option value="">{t('winder.allLines')}</option>
            {lines.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
      </div>

      {summary && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: t('winder.sumCounted'), value: summary.counted, color: '#10b981', bg: '#ecfdf5' },
            { label: t('winder.sumWaste'), value: `${summary.waste_count} · ${fmt(summary.waste_kg, 2)} kg`, color: '#d97706', bg: '#fffbeb' },
            { label: t('winder.sumPending'), value: summary.partial_pending, color: '#f59e0b', bg: '#fff7ed' },
            { label: t('winder.sumUnknown'), value: summary.unknown, color: '#dc3545', bg: '#fef2f2' },
            { label: t('winder.sumCycles'), value: summary.cycles, color: '#667eea', bg: '#eef2ff' },
          ].map(({ label, value, color, bg }) => (
            <div key={label} style={{ flex: 1, minWidth: 140, background: bg, border: `1px solid ${color}30`, borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {summary && summary.unknown > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
          ⚠ {t('winder.unknownHint', { count: summary.unknown })}
        </div>
      )}

      {/* Waste breakdown */}
      {wasteBy && summary && summary.waste_count > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
          {(['line', 'product', 'operator', 'shift'] as const).map(key => (
            <div key={key} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                {t('winder.wasteBy')} {t(`winder.by_${key}`)}
              </div>
              {wasteBy[key].map(g => (
                <div key={String(g.key)} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>{g.label || '—'}</span>
                  <strong style={{ whiteSpace: 'nowrap' }}>{g.count} · {fmt(g.kg, 2)} kg</strong>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={th}>{t('winder.colTime')}</th>
              <th style={th}>{t('winder.line')}</th>
              <th style={th}>{t('winder.colProduct')}</th>
              <th style={th}>{t('winder.colLength')}</th>
              <th style={th}>{t('winder.colResult')}</th>
              <th style={th}>{t('winder.colMass')}</th>
              <th style={th}>{t('winder.colOperator')}</th>
              <th style={th}>{t('winder.colShift')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('common.loading')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('winder.empty')}</td></tr>
            ) : rows.map(r => {
              const colour = r.item?.kolor?.includes('\n') ? r.item.kolor.split('\n').join('/') : r.item?.kolor;
              const short = r.nominal_length_m !== null && r.length_m < r.nominal_length_m;
              return (
                <tr key={r.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtTime(r.started_at)} – <strong>{fmtTime(r.ended_at)}</strong></td>
                  <td style={td}>{r.line_name ?? '—'}</td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{[r.item?.material, colour].filter(Boolean).join(' ') || '—'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.item?.indeks_kat}</div>
                  </td>
                  <td style={{ ...td, fontWeight: 700, color: short ? '#d97706' : '#1e293b', whiteSpace: 'nowrap' }}>
                    {fmt(r.length_m, 1)} m{r.nominal_length_m !== null && <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}> / {fmt(r.nominal_length_m)}</span>}
                  </td>
                  <td style={td}>{classBadge(r)}</td>
                  <td style={td}>{r.classification === 'partial' && r.operator_confirmed_partial ? `${fmt(r.estimated_weight_kg, 2)} kg` : '—'}</td>
                  <td style={td}>{r.user_name ?? '—'}</td>
                  <td style={td}>{r.shift === 'zmiana_1' ? 'Z1' : r.shift === 'zmiana_2' ? 'Z2' : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#6c757d', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap', letterSpacing: 0.5 };
const td: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };
const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#475569' };
const inp: React.CSSProperties = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minWidth: 150 };
const badge: React.CSSProperties = { display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' };

export default WinderCycles;
